/**
 * The reader's cleaner for server-written legal wording (review MR !1 AN38 reader side, W44).
 *
 * Angular's own sanitiser keeps an attribute allowlist with no `id` and no `name`, so a Word table of contents
 * (`href="#_Toc1"` beside `<h2 id="_Toc1">`) pointed at nothing once the text was drawn. This cleaner is an ALLOWLIST that mirrors the
 * server's `app/utils/legalHtml.js`, so what the reader keeps is what the server keeps and nothing more:
 *
 * - it reads the text with `DOMParser` (an inert document: nothing runs, loads or fires while it is read) and BUILDS a new tree
 *   out of fresh elements, copying only allowed tags, allowed attributes and text. It never edits the parsed tree in place and
 *   never writes the parsed markup back, so a parser quirk cannot carry anything through;
 * - an element that is not XHTML (`svg`, `math` and everything inside them) is dropped WITH its content, as are the elements whose
 *   content is code, a form, a frame or media (`script`, `style`, `template`, `noscript`, `iframe`, `object`, `embed`, `form`,
 *   `input`, `button`, `select`, `textarea`, `xmp`, `noembed`, `noframes`, `plaintext`, `img`, `audio`, `video` ...). Any other
 *   unknown XHTML element loses its markup and keeps its words, exactly as the server's cleaner does;
 * - attributes: `href` on `a` only (http, https, mailto, tel or a `#fragment`; no user or password part), `id` on h1-h6 and p,
 *   `id` and `name` on `a`, `colspan` / `rowspan` on td and th, `title` on `a`. Everything else (`style`, `class`, `on...`) goes;
 * - an `id` or `name` must be a plain token and must not already be a property of `window` or `document`, so a document cannot
 *   clobber a global (DOM clobbering, W45) such as `window.Cashfree`.
 */
const ALLOWED_TAGS = new Set([
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr', 'div', 'span', 'blockquote', 'pre', 'code',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
    'ul', 'ol', 'li',
    'a',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]);

/** Dropped together with everything inside them. */
const DROPPED_WITH_CONTENT = new Set([
    'script', 'style', 'template', 'noscript', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'param',
    'form', 'input', 'button', 'select', 'option', 'optgroup', 'textarea', 'label', 'fieldset', 'datalist', 'output',
    'xmp', 'noembed', 'noframes', 'plaintext', 'listing', 'svg', 'math',
    'img', 'picture', 'source', 'track', 'audio', 'video', 'canvas', 'map', 'area', 'dialog',
    'link', 'meta', 'base', 'title', 'head', 'slot',
]);

const ANCHOR_TARGET_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'];
const TAG_ATTRIBUTES: Record<string, string[]> = {
    a: ['href', 'title', 'id', 'name'],
    th: ['colspan', 'rowspan'],
    td: ['colspan', 'rowspan'],
};
ANCHOR_TARGET_TAGS.forEach((tag) => (TAG_ATTRIBUTES[tag] = ['id']));

const XHTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';
const ANCHOR_NAME = /^[A-Za-z_][\w:.-]{0,99}$/;
const SAME_PAGE_ANCHOR = /^#[A-Za-z_][\w:.-]{0,99}$/;
const MAIL_OR_PHONE = /^(mailto|tel):\S+$/i;
const CELL_SPAN = /^\d{1,2}$/;
/** Globals the app itself waits for, which a document must never be able to pre-empt with an element of that name. */
const APP_GLOBALS = ['Cashfree', 'Hammer'];
/** The highest character code a browser ignores inside an address (whitespace and control characters). */
const LAST_IGNORED_CODE = 32;

const asBrowserReadsIt = (address: string): string =>
    Array.from(address).filter((character) => character.charCodeAt(0) > LAST_IGNORED_CODE).join('');

/**
 * The address a reader may follow, or null. Relative addresses are refused: they would resolve against `<base href="/">`.
 * The characters a browser ignores are removed ONLY to decide the scheme; what is written out is what the URL parser makes of the
 * address as typed (`new URL(value).href`), so a space inside a path becomes `%20` and is never silently deleted (review MR !1 round 8, nit 13).
 */
export function safeLegalHref(raw: string): string | null {
    const stripped = asBrowserReadsIt(raw);
    if (SAME_PAGE_ANCHOR.test(stripped)) {
        return stripped;
    }
    const isMailOrPhone = MAIL_OR_PHONE.test(stripped);
    if (!isMailOrPhone && !/^https?:/i.test(stripped)) {
        return null;
    }
    try {
        const url = new URL(raw.trim());
        const protocol = url.protocol;
        const allowed = isMailOrPhone ? protocol === 'mailto:' || protocol === 'tel:' : protocol === 'http:' || protocol === 'https:';
        return allowed && !url.username && !url.password ? url.href : null;
    } catch {
        return null;
    }
}

/** True when an element of this name could replace a property of `window` or `document`. */
export function clobbersGlobal(token: string): boolean {
    return token in window || token in document || APP_GLOBALS.includes(token);
}

function copyAttributes(source: Element, target: Element, tag: string): void {
    const allowed = TAG_ATTRIBUTES[tag] ?? [];
    Array.from(source.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        if (!allowed.includes(name)) {
            return;
        }
        const value = attribute.value;
        if (name === 'href') {
            const href = safeLegalHref(value);
            if (href) {
                target.setAttribute('href', href);
            }
        } else if (name === 'id' || name === 'name') {
            const token = value.trim();
            if (ANCHOR_NAME.test(token) && !clobbersGlobal(token)) {
                target.setAttribute(name, token);
            }
        } else if (name === 'colspan' || name === 'rowspan') {
            if (CELL_SPAN.test(value.trim())) {
                target.setAttribute(name, value.trim());
            }
        } else if (name === 'title') {
            target.setAttribute('title', value);
        }
    });
}

function copyChildren(source: Node, target: Node, out: Document): void {
    source.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            target.appendChild(out.createTextNode(child.textContent ?? ''));
            return;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const element = child as Element;
        const tag = element.localName.toLowerCase();
        if (element.namespaceURI !== XHTML_NAMESPACE || DROPPED_WITH_CONTENT.has(tag)) {
            return;
        }
        if (!ALLOWED_TAGS.has(tag)) {
            copyChildren(element, target, out);
            return;
        }
        const clean = out.createElement(tag);
        copyAttributes(element, clean, tag);
        copyChildren(element, clean, out);
        target.appendChild(clean);
    });
}

/**
 * `pagePath` (pathname + search of the page the text is drawn on) is put in front of every same-page `#fragment` link, so a
 * middle-click or "open in new tab" opens THIS page at that heading and not `/#fragment` against `<base href="/">`.
 */
export function cleanLegalHtmlForReader(html: string | null | undefined, pagePath = ''): string {
    if (typeof html !== 'string' || !html) {
        return '';
    }
    const parsed = new DOMParser().parseFromString(`<!doctype html><body>${html}`, 'text/html');
    const out = document.implementation.createHTMLDocument('');
    copyChildren(parsed.body, out.body, out);
    if (pagePath) {
        out.body.querySelectorAll('a[href^="#"]').forEach((link) => link.setAttribute('href', pagePath + link.getAttribute('href')));
    }
    return out.body.innerHTML;
}
